import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';

const Step3ProfilePicture = ({ profilePicture, onUpdate, onNext, onBack }) => {
  const [selectedImage, setSelectedImage] = useState(profilePicture);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setSelectedImage(imageData);
        onUpdate({ profilePicture: imageData });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleImageSelect(file);
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDragIn = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setDragActive(true);
  };

  const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) handleImageSelect(e.dataTransfer.files[0]);
  };

  const openFileDialog = () => { fileInputRef.current?.click(); };

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>Choose your profile picture</StepTitle>
          <StepSubtitle>Upload an image or use our default avatar</StepSubtitle>
        </StepHeader>

        <ImageUploadContainer>
          <UploadArea
            $dragActive={dragActive}
            $hasImage={!!selectedImage}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={openFileDialog}
          >
            {selectedImage ? (
              <SelectedImage src={selectedImage} alt="Profile" />
            ) : (
              <UploadContent>
                <UploadIcon>
                  <ImageIcon size={48} />
                </UploadIcon>
                <UploadText>
                  <strong>Choose image</strong>
                  <span>or drag and drop</span>
                </UploadText>
                <UploadSubtext>PNG, JPG up to 5MB</UploadSubtext>
              </UploadContent>
            )}
          </UploadArea>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} style={{ display: 'none' }} />
        </ImageUploadContainer>

        <ImagePreviewContainer>
          <PreviewTitle>Preview:</PreviewTitle>
          <PreviewGrid>
            <PreviewItem>
              <PreviewLabel>Profile Picture</PreviewLabel>
              <PreviewImage $src={selectedImage}>
                {!selectedImage && <DefaultAvatar>👤</DefaultAvatar>}
              </PreviewImage>
            </PreviewItem>
            
            <PreviewItem>
              <PreviewLabel>Page Header</PreviewLabel>
              <HeaderPreview $src={selectedImage}>
                {!selectedImage && <DefaultAvatar>👤</DefaultAvatar>}
                <HeaderOverlay>
                  <HeaderText>Your Display Name</HeaderText>
                </HeaderOverlay>
              </HeaderPreview>
            </PreviewItem>
          </PreviewGrid>
        </ImagePreviewContainer>

        <TipContainer>
          <TipIcon>💡</TipIcon>
          <TipText>You can change this anytime!</TipText>
        </TipContainer>

        <ButtonContainer>
          <BackButton onClick={onBack} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ArrowLeft size={20} /> Back
          </BackButton>
          
          <NextButton
            onClick={() => { if(selectedImage) onNext(); }}
            disabled={!selectedImage}
            $disabled={!selectedImage}
            whileHover={{ scale: selectedImage ? 1.05 : 1 }}
            whileTap={{ scale: selectedImage ? 0.95 : 1 }}
          >
            Next
          </NextButton>
        </ButtonContainer>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`width: 100%; max-width: 700px; display: flex; justify-content: center;`;

const StepCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
`;

const StepHeader = styled.div`text-align: center; margin-bottom: 3rem;`;

const StepTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const StepSubtitle = styled.p`
  font-size: 1.1rem;
  color: #cccccc;
  margin: 0;
`;

const ImageUploadContainer = styled.div`margin-bottom: 2rem;`;

const UploadArea = styled.div`
  border: 3px dashed ${props => props.$dragActive ? '#e0c16f' : props.$hasImage ? '#333333' : '#333333'};
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$dragActive ? 'rgba(224, 193, 111, 0.1)' : '#1a1a1a'};
  
  &:hover {
    border-color: #e0c16f;
    background: rgba(224, 193, 111, 0.1);
  }
`;

const SelectedImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #e0c16f;
  box-shadow: 0 8px 20px rgba(224, 193, 111, 0.2);
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const UploadIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
`;

const UploadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  strong { font-size: 1.2rem; color: #ffffff; }
  span { font-size: 1rem; color: #cccccc; }
`;

const UploadSubtext = styled.p`font-size: 0.9rem; color: #888888; margin: 0;`;

const ImagePreviewContainer = styled.div`margin-bottom: 2rem;`;

const PreviewTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const PreviewGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 2rem;`;

const PreviewItem = styled.div`text-align: center;`;

const PreviewLabel = styled.p`
  font-size: 0.9rem;
  color: #cccccc;
  margin: 0 0 0.5rem 0;
  font-weight: 500;
`;

const PreviewImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto;
  background: ${props => props.$src ? `url(${props.$src}) center/cover` : '#1a1a1a'};
  border: 2px solid #333333;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DefaultAvatar = styled.span`font-size: 2rem;`;

const HeaderPreview = styled.div`
  width: 200px;
  height: 100px;
  border-radius: 15px;
  margin: 0 auto;
  background: ${props => props.$src ? `url(${props.$src}) center/cover` : '#1a1a1a'};
  border: 2px solid #333333;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const HeaderOverlay = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
  padding: 1rem;
  text-align: center;
`;

const HeaderText = styled.p`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
`;

const TipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(224, 193, 111, 0.1);
  border-radius: 8px;
  margin-bottom: 2rem;
  border: 1px solid rgba(224, 193, 111, 0.2);
`;

const TipIcon = styled.span`font-size: 1.2rem;`;
const TipText = styled.p`font-size: 0.9rem; color: #e0c16f; margin: 0;`;

const ButtonContainer = styled.div`display: flex; gap: 1rem;`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: #cccccc;
  border: 2px solid #333333;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2a2a2a;
    border-color: #555555;
  }
`;

const NextButton = styled(motion.button)`
  flex: 1;
  padding: 1rem 2rem;
  background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'};
  color: ${props => props.$disabled ? '#666666' : '#000000'};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%)'};
  }
`;

export default Step3ProfilePicture;